import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, ShoppingBag, Settings, HelpCircle, LogOut, Menu, X, Shirt, Heart, PieChart, User, Target, BookOpen, Shield, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAdminViewAs } from "../context/AdminViewAsContext";
import { useCustomerStage } from "@/hooks/useCustomerStage";
import { getUserPagePermissions, PagePermissions } from "@/utils/pagePermissions";
import { stageDashboardMap } from "@/utils/navigationUtils";
import Logo from "./Logo";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import SharedAccessButton from "./SharedAccessButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
const NavBar = () => {
  const {
    user,
    signOut,
    sharedAccess,
    clearSharedAccess
  } = useAuth();
  const { viewAsUser, isAdminViewMode, clearViewAsUser, setViewAsUser } = useAdminViewAs();
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStylistOrAdmin, setIsStylistOrAdmin] = useState(false);
  const { stage } = useCustomerStage();
  const dashboardTarget = (stage && stageDashboardMap[stage]) || '/dashboard1';
  // Deep-link the top-nav sections into the customer's dashboard tabs (like the
  // account menu) instead of the standalone pages.
  const onDashboard = /^\/dashboard[0-9]+[a-z]?$/.test(location.pathname);
  const rawDashBase = onDashboard ? location.pathname : dashboardTarget;
  // Tabs live on dashboard2/3/4; dashboard1 has none, so send tab links to dashboard2.
  const dashBase = rawDashBase === '/dashboard1' ? '/dashboard2' : rawDashBase;
  const currentTab = new URLSearchParams(location.search).get("tab");
  const tabActive = (tab: string) => onDashboard && currentTab === tab;
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; username: string; full_name?: string; email?: string }>>([]);
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>({
    digital_closet: false,
    style_magazine: false,
    shopping_recommendations: false,
  });

  useEffect(() => {
    checkAdminStatus();
    checkStylistOrAdminStatus();
    loadPagePermissions();
    if (isAdmin) {
      loadAvailableUsers();
    }
  }, [user, isAdmin]);

  const loadPagePermissions = async () => {
    if (!user) {
      setPagePermissions({
        digital_closet: false,
        style_magazine: false,
        shopping_recommendations: false,
      });
      return;
    }

    try {
      const permissions = await getUserPagePermissions();
      setPagePermissions(permissions);
    } catch (error) {
      console.error('Error loading page permissions:', error);
      setPagePermissions({
        digital_closet: false,
        style_magazine: false,
        shopping_recommendations: false,
      });
    }
  };
  const checkAdminStatus = async () => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    try {
      const {
        data: profile
      } = await supabase.from('profiles').select('is_admin').eq('id', user.id).single();
      setIsAdmin(profile?.is_admin || false);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const checkStylistOrAdminStatus = async () => {
    if (!user) {
      setIsStylistOrAdmin(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'stylist']);

      if (error) throw error;
      setIsStylistOrAdmin(data && data.length > 0);
    } catch (error) {
      console.error('Error checking stylist/admin status:', error);
      setIsStylistOrAdmin(false);
    }
  };


  const loadAvailableUsers = async () => {
    try {
      // Fetch users who are ready for stylist (all 6 steps complete including wardrobe upload)
      const { data: progress, error: progressError } = await supabase
        .from('admin_onboarding_progress')
        .select('user_id')
        .eq('is_active', true)
        .eq('quiz_complete', true)
        .eq('scheduled_onboarding', true)
        .eq('fit_analysis_complete', true)
        .eq('onboarding_meeting_complete', true)
        .eq('payment_complete', true)
        .eq('wardrobe_upload_complete', true);

      if (progressError) throw progressError;

      const readyUserIds = progress?.map(p => p.user_id) || [];

      if (readyUserIds.length === 0) {
        setAvailableUsers([]);
        return;
      }

      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, email')
        .in('id', readyUserIds)
        .neq('id', user?.id); // Exclude current admin user

      if (error) throw error;
      
      // Sort alphabetically by full_name, fallback to username, then email
      const sortedProfiles = (profiles || []).sort((a, b) => {
        const nameA = (a.full_name || a.username || a.email || '').toLowerCase();
        const nameB = (b.full_name || b.username || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setAvailableUsers(sortedProfiles);
    } catch (error) {
      console.error('Error loading available users:', error);
      setAvailableUsers([]);
    }
  };
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  // Base nav items - filter based on user permissions
  const allNavItems = [{
    label: "Home",
    onClick: () => navigate("/"),
    active: isActive("/")
  }, {
    label: "Outfits",
    onClick: () => navigate(`${dashBase}?tab=outfits`),
    active: tabActive("outfits"),
    requiresPermission: "style_magazine" as keyof PagePermissions
  }, {
    label: "Recommendations",
    onClick: () => navigate(`${dashBase}?tab=recommendations`),
    active: tabActive("recommendations"),
    requiresPermission: "shopping_recommendations" as keyof PagePermissions
  }, {
    label: "Message your stylist",
    onClick: () => navigate("/stylist-chat"),
    active: isActive("/stylist-chat")
  }];

  // Digital closet nav item (for left side)
  const digitalClosetItem = {
    label: "My Closet",
    onClick: () => navigate(`${dashBase}?tab=closet`),
    active: tabActive("closet"),
    requiresPermission: "digital_closet" as keyof PagePermissions
  };

  // Filter nav items based on user permissions
  const navItems = allNavItems.filter(item => {
    // Always show Home and Message your stylist
    if (!item.requiresPermission) return true;
    
    // For shared access users, show all items
    if (sharedAccess) return true;
    
    // For logged in users, check permissions
    if (user) {
      return pagePermissions[item.requiresPermission] === true;
    }
    
    // For non-logged in users, don't show protected pages
    return false;
  });

  // Check if digital closet should be shown
  const showDigitalCloset = user && (sharedAccess || pagePermissions.digital_closet);
  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const handleSignOut = async () => {
    if (user) {
      await signOut();
    } else if (sharedAccess) {
      clearSharedAccess();
    }
    navigate("/");
  };
  const handleAuthAction = () => {
    if (user || sharedAccess) {
      handleSignOut();
    } else {
      navigate("/auth");
    }
  };
  return <nav className="border-b border-gray-200 py-2 px-4 md:px-8 bg-white sticky top-0 z-50">
      <div className="flex justify-between items-center h-16">
        <Logo />
        
        {mobile ? <>
            <Button variant="ghost" size="icon" className="md:hidden" onClick={handleToggleMenu} aria-label={isMenuOpen ? "Close menu" : "Open menu"}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            
            {isMenuOpen && <div className="fixed inset-0 top-[72px] bg-white z-50 p-4">
                <div className="flex flex-col space-y-2">
                  {/* Digital Closet first in mobile */}
                  {showDigitalCloset && (
                    <Button 
                      variant={digitalClosetItem.active ? "default" : "ghost"} 
                      className="w-full justify-start text-left" 
                      onClick={() => {
                        digitalClosetItem.onClick();
                        setIsMenuOpen(false);
                      }}
                    >
                      {digitalClosetItem.label}
                    </Button>
                  )}
                  
                  {navItems.map(item => <Button key={item.label} variant={item.active ? "default" : "ghost"} className="w-full justify-start text-left" onClick={() => {
              item.onClick();
              setIsMenuOpen(false);
            }}>
                      {item.label}
                    </Button>)}
                  
                  <Button 
                    variant={isActive(dashboardTarget) ? "default" : "ghost"} 
                    className="w-full justify-start text-left" 
                    onClick={() => {
                      navigate(dashboardTarget);
                      setIsMenuOpen(false);
                    }}
                  >
                    {"Dashboard"}
                  </Button>

                  {isStylistOrAdmin && (
                    <Button 
                      variant={isActive("/outfit-builder") ? "default" : "ghost"} 
                      className="w-full justify-start text-left" 
                      onClick={() => {
                        navigate("/outfit-builder");
                        setIsMenuOpen(false);
                      }}
                    >
                      Outfit Builder
                    </Button>
                  )}

                  {isStylistOrAdmin && (
                    <Button
                      variant={isActive("/stylist-hours") ? "default" : "ghost"}
                      className="w-full justify-start text-left"
                      onClick={() => {
                        navigate("/stylist-hours");
                        setIsMenuOpen(false);
                      }}
                    >
                      Log Hours
                    </Button>
                  )}

                  <div className="border-t my-2 pt-2">
                    {user ? <>
                        <Button variant="ghost" className="w-full justify-start text-left" onClick={() => {
                  navigate("/profile/edit");
                  setIsMenuOpen(false);
                }}>
                          <User className="mr-2 h-5 w-5" />
                          Profile
                        </Button>
                        <Button variant="ghost" className="w-full justify-start text-left" onClick={() => {
                  navigate("/contact");
                  setIsMenuOpen(false);
                }}>
                          <HelpCircle className="mr-2 h-5 w-5" />
                          Contact
                        </Button>
                        <Button variant="ghost" className="w-full justify-start text-left" onClick={() => {
                  handleSignOut();
                  setIsMenuOpen(false);
                }}>
                          <LogOut className="mr-2 h-5 w-5" />
                          Sign out
                        </Button>
                      </> : <Button className="w-full" onClick={() => {
                handleAuthAction();
                setIsMenuOpen(false);
              }}>
                        {sharedAccess ? "Clear Access" : "Sign In"}
                      </Button>}
                  </div>
                </div>
              </div>}
          </> : <>
            <div className="hidden md:flex space-x-1">
              {navItems.map(item => <Button key={item.label} variant={item.active ? "default" : "ghost"} onClick={item.onClick} className="h-9">
                  {item.label}
                </Button>)}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Digital Closet Button (desktop only) */}
              {showDigitalCloset && (
                <Button 
                  variant={digitalClosetItem.active ? "default" : "ghost"} 
                  onClick={digitalClosetItem.onClick} 
                  className="h-9"
                >
                  {digitalClosetItem.label}
                </Button>
              )}
              
              {/* Onboarding/Dashboard Button */}
              <Button 
                variant={isActive(dashboardTarget) ? "default" : "outline"} 
                size="sm" 
                className="shadow-md hover:shadow-lg transition-all duration-200" 
                onClick={() => navigate(dashboardTarget)}
              >
                {"Dashboard"}
              </Button>

              <SharedAccessButton />
              
              {/* Admin View As Dropdown */}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 gap-2"
                    >
                      <Shield className="h-4 w-4" />
                      {viewAsUser ? `Viewing as ${viewAsUser.username}` : 'Admin View'}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>View as User</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {viewAsUser && (
                      <>
                        <DropdownMenuItem onClick={clearViewAsUser}>
                          <User className="mr-2 h-4 w-4" />
                          <span>Return to Admin View</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <ScrollArea className="h-[300px]">
                      {availableUsers.map((profileUser) => (
                        <DropdownMenuItem
                          key={profileUser.id}
                          onClick={() => setViewAsUser(profileUser)}
                          className={viewAsUser?.id === profileUser.id ? "bg-accent" : ""}
                        >
                          <User className="mr-2 h-4 w-4" />
                          <span>{profileUser.full_name || profileUser.username || profileUser.email || 'Unnamed User'}</span>
                        </DropdownMenuItem>
                      ))}
                      {availableUsers.length === 0 && (
                        <DropdownMenuItem disabled>
                          <span className="text-muted-foreground">No users available</span>
                        </DropdownMenuItem>
                      )}
                    </ScrollArea>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {user ? <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Settings className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-popover/95 backdrop-blur-sm border border-border/50">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/profile/edit")}>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/contact")}>
                      <HelpCircle className="mr-2 h-4 w-4" />
                      <span>Contact</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu> : <Button onClick={handleAuthAction}>
                  {sharedAccess ? "Clear Access" : "Sign In"}
                </Button>}
            </div>
          </>}
      </div>
    </nav>;
};
export default NavBar;